<script setup>
import { usePageHead } from '@composables/use-page-head'
import { computed, ref, watch, onMounted } from 'vue'
import { useFavoritesStore } from '@state/stores/favorites'
import Layout from '@layouts/main-layout'
import MixTableBlock from '@components/mix-table-block.v2'
import LibraryHeader from '@components/library/library-header'
import LibrarySearchResults from '@components/library/library-search-results'
import LibrarySectionTabs from '@components/library/library-section-tabs'
import GenreFilterPills from '@components/library/genre-filter-pills'
import libraryPreferences from '@services/library-preferences-service'
import { useAnalytics } from '@composables/use-analytics'
import {
    ALL_GENRES,
    MIX_SORT_OPTIONS,
    deriveGenrePills,
    filterMixesByGenre,
    sortMixes,
} from '@utils/library-collections'

const SECTION = 'mixes'

const favoritesStore = useFavoritesStore()
const analyticsService = useAnalytics()
const searchQuery = ref('')
const likedMixes = computed(() => favoritesStore.likedMixes)
const mixesLoaded = computed(() => favoritesStore.isLoaded('mixes'))
const genrePills = computed(() => deriveGenrePills(likedMixes.value))

const selectedGenre = ref(ALL_GENRES)
// validate the persisted key — a stale/corrupt stored value would crash activeSortLabel's .find().label
const persistedSort = libraryPreferences.getSort(SECTION)
const sortKey = ref(
    MIX_SORT_OPTIONS.some(option => option.key === persistedSort) ? persistedSort : 'recentlyAdded'
)

const sortOptions = MIX_SORT_OPTIONS
const activeSortLabel = computed(() => sortOptions.find(option => option.key === sortKey.value).label)
const visibleMixes = computed(() =>
    sortMixes(filterMixesByGenre(likedMixes.value, selectedGenre.value), sortKey.value)
)

// unliking is reactive here — if the user unlikes every mix of the selected genre, its pill
// vanishes and the stale filter would show a false empty state (and, below 2 pills, an
// invisible, unclearable filter). Reset silently: not a user action, so NO analytics event.
watch(genrePills, pills => {
    if (selectedGenre.value !== ALL_GENRES && !pills.includes(selectedGenre.value)) {
        selectedGenre.value = ALL_GENRES
    }
})

function handleGenreChange(genre) {
    selectedGenre.value = genre
    analyticsService.libraryGenreFilterApplied(genre)
}

function handleSortChange(label) {
    const option = sortOptions.find(el => el.label === label)
    sortKey.value = option.key
    libraryPreferences.setSort(SECTION, option.key)
}

onMounted(() => {
    favoritesStore.fetchType(SECTION).catch(error => {
        // leave `loaded` untouched — the spinner (not a false empty state) is the FR21 failure mode
        console.error('Failed to refresh liked mixes', error)
    })
})

usePageHead({ title: 'Your Mixes' })
</script>

<template>
    <Layout>
        <BaseContent>
            <BaseSection padded>
                <LibraryHeader
                    title="Your Mixes"
                    :count="mixesLoaded ? likedMixes.length : null"
                    count-label="mixes"
                    :search-query="searchQuery"
                    @update:search-query="searchQuery = $event"
                />
                <LibrarySearchResults
                    v-if="searchQuery.trim()"
                    :query="searchQuery"
                />
                <template v-else>
                    <LibrarySectionTabs />
                    <div class="controls">
                        <GenreFilterPills
                            :genres="genrePills"
                            :selected="selectedGenre"
                            @change="handleGenreChange"
                        />
                        <BaseCustomSelect
                            class="sort"
                            prefix="Sort:"
                            :options="sortOptions.map(option => option.label)"
                            :active="activeSortLabel"
                            @change="handleSortChange"
                        />
                    </div>
                    <BaseSpinner v-if="!likedMixes.length && !mixesLoaded" />
                    <MixTableBlock
                        v-else-if="visibleMixes.length"
                        :mixes="visibleMixes"
                        genres
                        is-indexed-list
                    />
                    <BaseEmptyList
                        v-else
                        :error="{
                            title: 'No Mixes Yet',
                            paragraph: 'Like a mix? Tap the ♥ to add it to this list.',
                        }"
                    />
                </template>
            </BaseSection>
        </BaseContent>
    </Layout>
</template>

<style lang="scss" scoped>
@use '@design' as *;

.controls {
    display: flex;
    flex-wrap: wrap;
    gap: $s-s;
    align-items: center;
    justify-content: space-between;
    margin-bottom: $s-m;
}

.sort {
    margin-left: auto;
}
</style>
